import * as amplitude from '@amplitude/analytics-browser'

const AMPLITUDE_API_KEY = process.env['AMPLITUDE_API_KEY'] ?? ''

// Rate limiting for console warnings: max 3 per minute
const warningTimestamps: number[] = []
const MAX_WARNINGS_PER_MINUTE = 3
const warnThrottled = (message: string, error?: unknown) => {
  const now = Date.now()
  const oneMinuteAgo = now - 60000
  // Remove timestamps older than 1 minute
  while (
    warningTimestamps[0] !== undefined &&
    warningTimestamps[0] < oneMinuteAgo
  ) {
    warningTimestamps.shift()
  }
  if (warningTimestamps.length < MAX_WARNINGS_PER_MINUTE) {
    warningTimestamps.push(now)
    console.warn(message, error)
  }
}

// Standardized error message extraction
export const extractErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return undefined
}

const getPlatform = (): string | null => {
  try {
    return window.Telegram?.WebApp?.platform ?? null
  } catch {
    return null
  }
}

export const initAmplitude = () => {
  const environment = process.env['NODE_ENV'] ?? 'development'

  if (environment === 'development') {
    return
  }

  amplitude.init(AMPLITUDE_API_KEY, {
    serverZone: 'EU',
    autocapture: {
      attribution: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: true,
    },
  })
}

const track = (eventName: string, properties?: Record<string, unknown>) => {
  try {
    // Only track if Amplitude is properly initialized
    if (AMPLITUDE_API_KEY && process.env['NODE_ENV'] !== 'development') {
      const platform = getPlatform()
      const eventProperties = {
        ...properties,
        ...(platform ? { platform } : {}),
      }
      amplitude.track(eventName, eventProperties)
    }
  } catch (error) {
    // Silently handle amplitude errors to prevent console spam
    warnThrottled('Amplitude tracking failed:', error)
  }
}

// Wallet events
export const trackWalletConnectionStarted = () => {
  track('wallet_connection_started')
}

export const trackWalletConnected = (
  walletAddress: string,
  walletType: string,
) => {
  try {
    if (AMPLITUDE_API_KEY && process.env['NODE_ENV'] !== 'development') {
      console.log('Amplitude: Setting user ID to:', walletAddress)
      amplitude.setUserId(walletAddress)
    }
    track('wallet_connected', {
      wallet_address: walletAddress,
      wallet_type: walletType,
    })
  } catch (error) {
    warnThrottled('Amplitude wallet tracking failed:', error)
  }
}

export const trackWalletDisconnected = () => {
  try {
    track('wallet_disconnected')
    if (AMPLITUDE_API_KEY && process.env['NODE_ENV'] !== 'development') {
      amplitude.reset()
    }
  } catch (error) {
    warnThrottled('Amplitude wallet disconnect tracking failed:', error)
  }
}

// Box view events
export const trackBoxViewed = (
  boxId: string,
  source: 'feed' | 'direct' | 'referral' | 'search',
) => {
  track('box_viewed', { box_id: boxId, source })
}

export const trackBoxListViewed = (
  filterType: 'all' | 'my' | 'my-votes' | 'finished',
  boxCount: number,
) => {
  track('box_list_viewed', { filter_type: filterType, box_count: boxCount })
}

export const trackBoxShared = (
  boxId: string,
  shareMethod: 'native' | 'clipboard',
) => {
  track('box_shared', { box_id: boxId, share_method: shareMethod })
}

// Vote events
export const trackVoteInitiated = (boxId: string, choiceIndex: number) => {
  track('vote_initiated', { box_id: boxId, choice_index: choiceIndex })
}

export const trackVoteAmountEntered = (
  boxId: string,
  amount: number,
  isPreset: boolean,
) => {
  track('vote_amount_entered', {
    box_id: boxId,
    amount,
    is_preset: isPreset,
  })
}

// Revenue tracking using Amplitude Revenue API for LTV/ARPU charts
const trackVoteRevenue = (
  boxId: string,
  choiceIndex: number,
  amount: number,
) => {
  if (AMPLITUDE_API_KEY && process.env['NODE_ENV'] !== 'development') {
    const revenue = new amplitude.Revenue()
      .setProductId('vote')
      .setPrice(amount * 0.1) // 10% platform fee
      .setQuantity(1)
      .setRevenueType('vote_fee')
      .setEventProperties({
        box_id: boxId,
        choice_index: choiceIndex,
        gross_amount: amount,
      })
    amplitude.revenue(revenue)
  }
}

export const trackVoteSuccess = (
  boxId: string,
  choiceIndex: number,
  amount: string,
) => {
  const amountNum = parseFloat(amount)
  track('vote_success', { box_id: boxId, choice_index: choiceIndex, amount })
  if (!isNaN(amountNum) && amountNum > 0) {
    trackVoteRevenue(boxId, choiceIndex, amountNum)
  }
}

export const trackVoteFailed = (
  boxId: string,
  choiceIndex: number,
  error?: string,
) => {
  track('vote_failed', { box_id: boxId, choice_index: choiceIndex, error })
}

// Box creation events
export const trackBoxCreationStarted = () => {
  track('box_creation_started')
}

export const trackBoxCreationSuccess = (boxAddress?: string) => {
  track('box_creation_success', boxAddress ? { box_address: boxAddress } : {})
}

export const trackBoxCreationFailed = (error?: string) => {
  track('box_creation_failed', { error })
}

// Reward claim events
export const trackRewardClaimInitiated = (boxId: string) => {
  track('reward_claim_initiated', { box_id: boxId })
}

export const trackRewardClaimSuccess = (boxId: string, amount: string) => {
  track('reward_claim_success', { box_id: boxId, amount })
}

export const trackRewardClaimFailed = (boxId: string, error?: string) => {
  track('reward_claim_failed', { box_id: boxId, error })
}

// Bonus withdrawal events
export const trackBonusWithdrawalSuccess = (amount: string) => {
  track('bonus_withdrawal_success', { amount })
}

export const trackBonusWithdrawalFailed = (error?: string) => {
  track('bonus_withdrawal_failed', { error })
}

// Onboarding events
export const trackOnboardingCompleted = () => {
  track('onboarding_completed')
}

export const trackOnboardingSkipped = () => {
  track('onboarding_skipped')
}

// Referral events
export const trackReferralLinkCopied = () => {
  track('referral_link_copied')
}

export const trackReferralLinkShared = () => {
  track('referral_link_shared')
}

// Task events
export const trackTaskCompleted = (taskId: string) => {
  track('task_completed', { task_id: taskId })
}

// Vote dialog events
export const trackVoteDialogOpened = (boxId: string) => {
  track('vote_dialog_opened', { box_id: boxId })
}

export const trackVoteDialogClosed = (boxId: string, voted: boolean) => {
  track('vote_dialog_closed', { box_id: boxId, voted })
}
